import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react"
import moment from "moment"
import { useLocation } from "react-router-dom"
import { useGameplayConfig } from "./gameplayConfig"
import ProctoringApi from "services/apis/proctoring"
import { storage } from "services/config/storage"
import { useIsMobileDevice } from "hooks"
import { isScreenProctoredEnabled } from "utils/helper"
import { PERMISSION_ERRORS } from "constants/globalConstants"
import { HIDE_GAME_IFRAME_EVENT, SHOW_GAME_IFRAME_EVENT } from "constants/helperModalConstants"

const ScreenRecordingContext = createContext(null)

export const ScreenRecordingProvider = ({ children }) => {
    const [screenRecorder, setScreenRecorder] = useState(null)
    const [recorderState, setRecorderState] = useState("")
    const [isPermissionModalVisible, setIsPermissionModalVisible] = useState("")
    const screenStreamRef = useRef(null)
    const apiFailureCountRef = useRef(0)

    const { isScreenProctored } = useGameplayConfig()
    const isMobile = useIsMobileDevice()
    const location = useLocation()

    const mimeType = MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4"
    const fileExtension = mimeType.split('/')[1]

    useEffect(() => {
        if (isPermissionModalVisible) {
            window.dispatchEvent(new Event(HIDE_GAME_IFRAME_EVENT))
            handleRecordingCleanup()
        } else {
            window.dispatchEvent(new Event(SHOW_GAME_IFRAME_EVENT))
        }
    }, [isPermissionModalVisible, screenStreamRef.current])

    useEffect(() => {
        const { pathname } = window.location
        const page = pathname.split('/')[1]
        if (!screenRecorder && page === "games") initialiseAndStartRecording()
    }, [isScreenProctored, isMobile, screenRecorder])

    useEffect(() => {
        if (screenRecorder) {
            startRecording()
        }
    }, [screenRecorder])

    useEffect(() => {
        try {
            if (recorderState === "recording") {
                RECORDING_CONST.timeout = setTimeout(() => {
                    screenRecorder?.stop()
                    setRecorderState("inactive")
                }, RECORDING_CONST.recordingInterval)
            } else if (recorderState === "inactive") {
                screenRecorder?.start()
                setRecorderState("recording")
            }
        } catch {
            setIsPermissionModalVisible(true)
        }

        return () => clearTimeout(RECORDING_CONST.timeout)
    }, [recorderState])

    useEffect(() => {
        return () => {
            handleRecordingCleanup()
        }
    }, [])

    useEffect(() => {
        if (location.pathname.includes("end")) handleRecordingCleanup()
    }, [location])

    const initialiseAndStartRecording = async () => {
        try {
            if (isScreenProctoredEnabled({ isScreenProctored, isMobile })) {
                const { screenStream } = await getScreenStream()
                if (!screenStream) throw new Error()
                screenStreamRef.current = screenStream
                const screenMediaRecorder = new MediaRecorder(screenStreamRef.current, { mimeType })
                screenMediaRecorder.ondataavailable = (e) => handleDataAvailable(e)

                setScreenRecorder(screenMediaRecorder)
                return screenStream
            }
        } catch (err) { return null }
    }

    const getScreenStream = async () => {
        try {
            const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
            const videoTrack = screenStream.getVideoTracks()[0]
            const settings = videoTrack.getSettings()
            if (settings.displaySurface !== "monitor") {
                setIsPermissionModalVisible(PERMISSION_ERRORS.ENTIRE_SCREEN)
                screenStream?.getTracks()?.forEach(track => {
                    track?.stop()
                })
                return { screenStream: null }
            } else {
                setIsPermissionModalVisible("")
                videoTrack.addEventListener('ended', handleScreenEnded)
                return { screenStream }
            }
        } catch (err) {
            console.log("Screen Permission Error:", err)
            if (err?.toString()?.toLowerCase()?.includes(PERMISSION_ERRORS.USER_GESTURE)) {
                setIsPermissionModalVisible(PERMISSION_ERRORS.USER_GESTURE)
            } else {
                setIsPermissionModalVisible(PERMISSION_ERRORS.SCREEN_PERMISSION_DENIED)
            }
            return { screenStream: null }
        }
    }

    const startRecording = () => {
        try {
            if (screenRecorder?.state !== "recording") {
                screenRecorder?.start()
                setRecorderState("recording")
            }
        } catch {
            setIsPermissionModalVisible(true)
        }
    }

    const stopRecording = () => {
        try {
            if (screenRecorder?.state !== "inactive") {
                screenRecorder?.stop()
                setRecorderState("")
                clearTimeout(RECORDING_CONST.timeout)
                screenStreamRef.current?.getTracks()?.forEach(track => {
                    track?.stop()
                })
            }
        } catch (err) {
            console.log(err)
            setIsPermissionModalVisible(true)
        }
    }

    const handleDataAvailable = useCallback(async (event) => {
        if (!event.data.size) return;

        const currentTime = new Date();
        const fileName = `screen|${moment(currentTime).format("DD-MM-YYYY_HH-mm-ss")}.${fileExtension}`
        const file = new File([event.data], fileName, { type: mimeType })

        let currentVideoOrder = Number(storage.get.screenVideoOrderCount())
        let videoSessionId = storage.get.screenVideoSessionId()
        storage.set.screenVideoOrderCount(currentVideoOrder + 5)

        try {
            await ProctoringApi.sendUserVideo({
                file,
                fileName,
                startTime: currentVideoOrder,
                endTime: currentVideoOrder + 5,
                duration: 5,
                sessionId: videoSessionId,
                type: "video",
            })
            apiFailureCountRef.current = 0
        } catch (err) {
            console.log("Upload error:", err.message)
            apiFailureCountRef.current += 1
            if (apiFailureCountRef.current >= 10) {
                setIsPermissionModalVisible(PERMISSION_ERRORS.API_FAILURE)
            }
        }
    }, [screenRecorder])

    const handleScreenEnded = () => {
        setIsPermissionModalVisible(PERMISSION_ERRORS.STOP_SHARING)
        stopRecording()
    }

    const handleRecordingCleanup = () => {
        stopRecording()
        setScreenRecorder(null)
        screenStreamRef.current = null
    }

    return (
        <ScreenRecordingContext.Provider
            value={{
                screenRecorder,
                initialiseAndStartRecording,
                isPermissionModalVisible,
            }}

        >
            {children}
        </ScreenRecordingContext.Provider>
    )
}

export const useScreenRecordingContext = () => useContext(ScreenRecordingContext)

const RECORDING_CONST = {
    timeout: "",
    recordingInterval: 5000,
    duration: 5,
}